<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col v-if="computedIsFetched">
      <card-title-row :straight-title="additionalModuleName" :amount="1" :is-plural="true">
        <template #buttons>
          <base-rounded-button
            v-if="hasPermission"
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.createInvestment')"
            @click="toggleInvestmentAdding"
          />
        </template>
      </card-title-row>
      <card-title-row
        is-subtitle
        translation-path="sidebar.visualisationOnMap"
        :amount="1"
        :is-plural="true"
        class="pt-2"
      />
      <sidebar-row class="pt-2">
        <sidebar-select-row
          translation-path="sidebar.visualizationType"
          font-size="14px"
          @input="handleStyleChange"
          :value="currentStyle"
          :select-props="styleSelectProps"
        />
      </sidebar-row>
      <module-legend ref="moduleLegend" border-bottom module-name="investments" />
      <investment-category-visualization
        :items="categories"
        class="pt-4"
        @input="setFilter"
        :picked="picked"
      ></investment-category-visualization>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import SidebarSelectRow from '@/components/SidebarSelectRow';
import InvestmentCategoryVisualization from '@/components/InvestmentCategoryVisualization';
import SidebarRow from '@/components/SidebarRow';
import ModuleLegend from '@/components/ModuleLegend';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'Investments',
  components: {
    MainLoader,
    ModuleLegend,
    SidebarRow,
    InvestmentCategoryVisualization,
    CardTitleRow,
    SidebarSelectRow,
  },
  data: () => ({
    picked: [],
    isFetched: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.INVESTMENTS.verbose_name'),
    permissions: get('users/toolsPermissions'),
    styles: get('styles/styles@:dataSourceName'),
    layerId: get('admin/modulesMapping@investments.layer_id'),
    dataSourceName: get('admin/modulesMapping@investments.datasource_name'),
    investments: get('investments/investments'),
    categories: get('investments/categories'),
    currentStyle: get('styles/currentStyle@:dataSourceName'),
    defaultStyle: get('styles/defaultStyle@:dataSourceName'),
    loaded: get('modules/loaded@:dataSourceName'),

    computedIsFetched() {
      return this.isFetched && this.loaded;
    },
    computedSelectStyles() {
      return [this.defaultStyle, ...this.$_sortObjectsByAttribute([...this.styles], 'name')];
    },
    styleSelectProps() {
      return {
        items: this.computedSelectStyles,
        itemText: 'name',
        itemValue: 'id',
        returnObject: true,
      };
    },
    permissionsInvestments() {
      return this.permissions['INVESTMENTS'];
    },
    hasPermission() {
      return this.permissionsInvestments.main_value > 1;
    },
  },
  methods: {
    getStyles: call('styles/getStyles'),
    getInvestments: call('investments/getInvestments'),
    setCurrentStyle: call('styles/setCurrentStyle'),
    getCategories: call('investments/getCategories'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    setFilter(value) {
      this.picked = value;
      localStorage.setItem('investmentCategories', JSON.stringify(value));
      this.$root.$emit('setSidebarFilters', { category: this.picked.length < 1 ? ['-'] : this.picked });
    },
    toggleInvestmentAdding() {
      this.$router.push({
        name: 'investment',
        params: { fid: 'new' },
      });
    },
    async handleStyleChange(style) {
      await this.setCurrentStyle({ dataSource: this.dataSourceName, style: style });
      this.$root.$emit('investmentsStyleChanged', style);
      await this.$refs.moduleLegend.getModuleStyle();
    },
    async init() {
      await Promise.all([
        this.getInvestments(),
        this.getCategories(),
        this.getStyles(this.dataSourceName),
        this.getFeaturesLayer(this.layerId),
      ]);
      const storageItems = JSON.parse(localStorage.getItem('investmentCategories'));
      this.picked = localStorage.getItem('investmentCategories') ? storageItems : [];
      this.setFilter(this.picked);
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.borderBottom {
  @include border(bottom);
}
</style>
